import React from 'react';
import { Select, Spin, Typography, Space, Row, Col, Avatar, Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { getPlatformConfig } from '../../utils/channelUtils';

const { Option } = Select;
const { Text } = Typography;

const ChannelSelect = ({ channels, loading, value, onChange, onSearch }) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        border: isDarkMode ? '#434343' : '#f0f0f0',
        hoverBg: isDarkMode ? '#2a2a2a' : '#fafafa',
        shadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
        selectedBg: isDarkMode ? '#1f1f1f' : '#e6f7ff'
    };

    const selectDropdownRender = (menu) => (
        <div style={{
            background: themeColors.background,
            borderRadius: '8px',
            boxShadow: themeColors.shadow,
            border: `1px solid ${themeColors.border}`
        }}>
            {menu}
        </div>
    );

    const ellipsisStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        color: themeColors.text,
        backgroundColor: 'transparent'
    };

    // Custom styles for the CSS overrides
    const customStyles = `
        .ant-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
            background-color: ${themeColors.selectedBg} !important;
            color: ${themeColors.text} !important;
        }
        
        .ant-select-dropdown .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
            background-color: ${themeColors.hoverBg} !important;
        }

        .ant-select-dropdown .ant-typography {
            background-color: transparent !important;
            color: ${themeColors.text} !important;
        }
    `;

    const renderChannelAvatar = (channel) => {
        const platform = getPlatformConfig(channel.channel_type, t, isDarkMode);
        const PlatformIcon = platform.icon;

        return (
            <Badge
                count={
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: platform.color,
                        border: `1px solid ${themeColors.background}`,
                    }}>
                        <PlatformIcon style={{ fontSize: 8, color: 'white' }} />
                    </div>
                }
                offset={[-2, 18]}
            >
                <Avatar
                    size={22}
                    src={channel.picture_url}
                    icon={<PlatformIcon />}
                    style={{
                        backgroundColor: channel.picture_url ? 'transparent' : `${platform.color}15`,
                        color: platform.color,
                        border: 'none',
                    }}
                />
            </Badge>
        );
    };

    return (
        <>
            <style>{customStyles}</style>
            <Select
                showSearch
                loading={loading}
                value={value}
                onChange={onChange}
                onSearch={onSearch}
                style={{ width: '100%' }}
                placeholder={t('select_a_channel')}
                optionLabelProp="label"
                notFoundContent={loading ? <Spin size="small" style={{ color: themeColors.text }} /> : null}
                filterOption={(input, option) =>
                    (option?.labelString ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={selectDropdownRender}
                popupStyle={{
                    padding: '8px 0',
                    backgroundColor: themeColors.background
                }}
                listItemHeight={72}
                listHeight={320}
                suffixIcon={loading ? <Spin size="small" style={{ color: themeColors.text }} /> : undefined}
                className={isDarkMode ? 'dark-mode-select' : 'light-mode-select'}
            >
                {channels.map(channel => (
                    <Option
                        key={channel.id}
                        value={channel.id}
                        labelString={channel.name} // Custom prop for filtering
                        label={
                            <Row align="middle" style={{ width: '100%', backgroundColor: 'transparent' }}>
                                <Space size={12} style={{ backgroundColor: 'transparent' }}>
                                    {renderChannelAvatar(channel)}
                                    <Text strong style={ellipsisStyle}>{channel.name}</Text>
                                </Space>
                            </Row>
                        }
                    >
                        <Row align="middle" style={{
                            padding: '4px 0',
                            width: '100%',
                            backgroundColor: 'transparent',
                        }} wrap={false}>
                            <Col flex="auto" style={{ minWidth: 0, marginInlineEnd: '16px', overflow: 'hidden', backgroundColor: 'transparent' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%', backgroundColor: 'transparent' }}>
                                    <div style={{ flexShrink: 0, marginInlineEnd: '12px', backgroundColor: 'transparent' }}>
                                        {renderChannelAvatar(channel)}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1, backgroundColor: 'transparent' }}>
                                        <Text strong style={ellipsisStyle}>{channel.name}</Text>
                                        <Text style={{
                                            ...ellipsisStyle,
                                            fontSize: '12px',
                                            color: themeColors.secondaryText
                                        }}>
                                            {getPlatformConfig(channel.channel_type, t, isDarkMode).name}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Option>
                ))}
            </Select>
        </>
    );
};

export default ChannelSelect;
